import React from "react";

import { Form, Formik } from "formik";
import { useDispatch, useSelector } from "react-redux";

import { closeConfirmModal, openConfirmModal } from "actions";

import { BusinessLabelAction } from "actions/businessLabel";

import { AdminLink } from "components/AdminLink/AdminLink";
import AuditLogLink from "components/AuditLog/AuditLogLink";
import { Button, DeleteButton, SecondaryButton } from "components/Form";
import { useSubmitHandler } from "components/Form/FormikControls";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { BusinessLabelForm } from "components/BusinessLabel/Form";

import { AuditLogTypes } from "constants/auditLog";
import { ApiModel } from "constants/loading";
import { ModalTypes } from "constants/navigation";
import { BusinessLabelPermission } from "constants/permissions";

import { deepObjectChanges } from "lib/compare";
import { hasPermission } from "lib/permissions";

import { getBusinessLabelById } from "selectors/businessLabel";
import { getContextByModalType } from "selectors";

import { useCanAddBusinessLabelPermission } from "hooks";

import { BusinessLabelValidationSchema } from "./validationSchema";

interface FooterProps {
  onClose: () => void;
  id: string | null;
}

function Footer(props: FooterProps) {
  const { onClose, id } = props;
  const [isSubmitEnabled, setIsSubmitEnabled] = React.useState(false);

  const businessLabel = useSelector(getBusinessLabelById(id));

  const hasCreatePermission = useCanAddBusinessLabelPermission();

  const hasEditPermission: boolean = hasPermission(
    businessLabel,
    BusinessLabelPermission.update,
  );

  const hasSavePermission: boolean = id
    ? hasEditPermission
    : hasCreatePermission;

  const hasDeletePermission: boolean = hasPermission(
    businessLabel,
    BusinessLabelPermission.delete,
  );

  useSubmitHandler(false, setIsSubmitEnabled);
  const dispatch = useDispatch();

  function onClickConfirmDelete() {
    dispatch(BusinessLabelAction.delete(id));
    dispatch(closeConfirmModal());
    onClose();
  }

  function onClickCancelDelete() {
    dispatch(closeConfirmModal());
  }

  function onClickDelete() {
    dispatch(
      openConfirmModal({
        title: "Are you sure?",
        message: `Deleting this business label will remove it from all businesses it is currently applied to. This cannot be undone.`,
        actions: [
          {
            label: "Cancel",
            onClick: onClickCancelDelete,
            secondary: true,
          },
          {
            label: "Delete Business Label",
            onClick: onClickConfirmDelete,
            secondary: false,
          },
        ],
      }),
    );
  }

  return (
    <>
      <SecondaryButton type="button" onClick={onClose}>
        Close
      </SecondaryButton>
      {businessLabel && (
        <DeleteButton
          type="button"
          data-tour="delete"
          disabled={!hasDeletePermission}
          onClick={onClickDelete}
        >
          Delete
        </DeleteButton>
      )}
      <Button
        type="submit"
        data-tour="save"
        disabled={!isSubmitEnabled || !hasSavePermission}
      >
        Save
      </Button>
    </>
  );
}

interface BusinessLabelModalProps {
  onClose: () => void;
}

interface BusinessLabelFormValues {
  id: string;
  name: string;
  archetype: string | null;
}

export function BusinessLabelModal(props: BusinessLabelModalProps) {
  const { onClose } = props;
  const dispatch = useDispatch();
  const context = useSelector(getContextByModalType(ModalTypes.BusinessLabel));
  const id: string | null = context?.id ?? null;
  const businessLabel = useSelector(getBusinessLabelById(id));

  const initialValues: Partial<BusinessLabelFormValues> = {
    archetype: null,
    ...(businessLabel || {}),
  };

  function onSubmit(values: BusinessLabelFormValues) {
    if (id) {
      dispatch(
        BusinessLabelAction.update(
          { id, ...deepObjectChanges(initialValues, values) },
          { changeReason: "Updated from Edit Business Label modal" },
        ),
      );
    } else {
      dispatch(BusinessLabelAction.create(values));
    }
    onClose();
  }

  return (
    <WaitForSync requiredData={[ApiModel.BUSINESS_LABELS]}>
      <ZoomyDialog open onClose={onClose} maxWidth="xs" fullWidth>
        <DialogTitle onClose={onClose}>
          {id && (
            <AuditLogLink
              auditLogType={AuditLogTypes.BUSINESS_LABEL}
              dataId={id}
              returnTo={window.location.hash}
            />
          )}
          &nbsp;{id ? "Edit" : "Create"} Business Label
          <AdminLink model="businesslabel" cbid={id} />
        </DialogTitle>

        <Formik
          initialValues={initialValues}
          onSubmit={onSubmit}
          validationSchema={BusinessLabelValidationSchema}
        >
          <Form>
            <DialogContent dividers>
              <BusinessLabelForm />
            </DialogContent>
            <DialogActions>
              <Footer id={id} onClose={onClose} />
            </DialogActions>
          </Form>
        </Formik>
      </ZoomyDialog>
    </WaitForSync>
  );
}
